import { matchToLiveView } from "../../matchScoringBridge";
import { ACTIVITY_COMMAND_TYPES } from "../../domain/cmoe/activity/activityCommandTypes";
import { livePayloadToActivityPayloadFields } from "../../domain/cmoe/activity/activityLivePayloadMapper";

/** @type {import('../../domain/cmoe/activity/activityModuleContract').ActivityModule} */
export const badmintonRallyV1Module = {
  moduleId: "badminton.rally",
  moduleVersion: "1",

  validateAttach(ctx) {
    const keys = Object.keys(ctx.bindings || {});
    if (keys.length < 2) {
      return { ok: false, reason: "bindings_require_two_sides" };
    }
    return { ok: true };
  },

  createInitialState() {
    return {
      activityVersion: 1,
      eventSeq: 0,
      payloadSchemaVersion: 1,
      payload: { scoringMatch: null },
    };
  },

  handleCommand(ctx, command) {
    const snap = ctx.snapshot;
    if (command.baseActivityVersion !== snap.activityVersion) {
      return { status: "conflict", snapshot: snap };
    }
    if (command.type === "noop") {
      return { status: "success", snapshot: snap, events: [] };
    }
    if (command.commandId && snap.lastCommandId === command.commandId) {
      return { status: "success", snapshot: snap, events: [] };
    }
    if (command.type === ACTIVITY_COMMAND_TYPES.SYNC_LIVE_STATE) {
      const live = command.payload?.live;
      if (!live || typeof live !== "object") {
        return { status: "reject", code: "INVALID_PAYLOAD" };
      }
      const fields = livePayloadToActivityPayloadFields(live);
      const nextPayload = {
        ...(snap.payload || {}),
        ...fields,
      };
      return {
        status: "success",
        snapshot: {
          ...snap,
          activityVersion: snap.activityVersion + 1,
          eventSeq: snap.eventSeq + 1,
          lastCommandId: command.commandId,
          payload: nextPayload,
        },
        events: [{ type: "LiveStateSynced", commandId: command.commandId }],
      };
    }
    if (command.type === ACTIVITY_COMMAND_TYPES.SET_ORCHESTRATION_PAUSE) {
      const paused = Boolean(command.payload?.paused);
      return {
        status: "success",
        snapshot: {
          ...snap,
          activityVersion: snap.activityVersion + 1,
          eventSeq: snap.eventSeq + 1,
          lastCommandId: command.commandId,
          payload: {
            ...(snap.payload || {}),
            orchestrationPaused: paused,
            status: paused ? "paused" : snap.payload?.status === "paused" ? "live" : snap.payload?.status,
          },
        },
        events: [{ type: "OrchestrationPauseSet", paused }],
      };
    }
    return { status: "reject", code: "UNKNOWN_COMMAND" };
  },

  serialize(snapshot) {
    return snapshot?.payload ?? null;
  },

  deserialize(raw) {
    if (!raw) return null;
    const payload =
      typeof raw === "object" && raw.scoringMatch !== undefined
        ? raw
        : { scoringMatch: raw };
    return {
      activityVersion: 1,
      eventSeq: 0,
      payloadSchemaVersion: 1,
      payload,
      lastCommandId: typeof raw?.lastCommandId === "string" ? raw.lastCommandId : undefined,
    };
  },

  isTerminal(snapshot) {
    const match = snapshot?.payload?.scoringMatch;
    return Boolean(match?.matchWinner);
  },

  exportForHistory(ctx) {
    const match = ctx.snapshot?.payload?.scoringMatch;
    const view = matchToLiveView(match);
    return {
      winnerSide: view.winner,
      sets: view.sets,
      scoringMatch: match,
    };
  },

  reconcileOffline({ serverSnapshot, localCommands }) {
    const seen = new Set();
    const retry = [];
    localCommands.forEach((cmd) => {
      if (seen.has(cmd.commandId)) return;
      seen.add(cmd.commandId);
      if (cmd.baseActivityVersion === serverSnapshot.activityVersion) {
        retry.push(cmd);
      }
    });
    return {
      clientSnapshot: serverSnapshot,
      retry,
      drop: [],
    };
  },
};
